import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { getUserToken, getUserInfo, getUserAuthList, getProjectIssuePrInfo } from '@/api/user';
import { getScoreTips } from '@/api/notice';
import type { accessType } from '@/stores/user/type';
import type { authListReqType, Profile } from '@/api/user/types';
import { getNotebookDetail } from '@/api/notebook';
import { EnumNotebookStatus, EnumComputedType } from '@/types/notebook.d';
import type { NotebookDetailResp } from '@/api/notebook/type';
import { ProFileStatus } from '@/views/OAuth/constant';
import { useAccount } from '@/utils/hooks/useAccount';

export interface AccountInfo {
  domain_id?: string;
  email?: string;
  id?: string;
  arts_id?: string;
  mobile?: string;
  avatar?: string;
  nickname?: string;
  username?: string;
  access_token?: string;
  refresh_token?: string;
  xauth_token?: string;
  isFollow?: boolean;
  atomgit_username?: string;
  [x: string]: any;
}

export const useAccountStore = defineStore('accountInfo', () => {
  const cacheInfo = localStorage.getItem('userInfo');
  const isLogin = ref(Boolean(localStorage.getItem('access_token')));
  const userInfo = cacheInfo ? JSON.parse(cacheInfo) : {};
  const checkProFile = ref(ProFileStatus.started); // 默认为不需要检查
  const accountInfo = reactive<AccountInfo>({
    access_token: localStorage.getItem('access_token') || '',
    refresh_token: localStorage.getItem('refresh_token') || '',
    xauth_token: localStorage.getItem('xauth_token') || '',
    ...userInfo
  });

  /** 新手状态 */
  const noviceStatus = computed(() => {
    if (!accountInfo.create_time) return null;
    const createDate = new Date(accountInfo.create_time);
    const sevenDaysLater = new Date(createDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    sevenDaysLater.setHours(23, 59, 59, 999);
    return {
      /** 是否是新手 注册7日内（包括第7天的最后一刻） */
      isNovice: Date.now() <= sevenDaysLater.getTime(),
      /** 新手期剩余天数 */
      residualDay: Math.ceil((sevenDaysLater.getTime() - Date.now()) / (1000 * 60 * 60 * 24) - 1)
    };
  });

  const access = ref<accessType>([]); // 用户在组织或项目中的权限
  const roleInfo = ref<any>(null); // 用户角色信息
  const enterpriseAccess = ref<accessType>([]); // 用户在企业中的权限
  const updateAccessStatus = ref(false);
  const isEnterpriseMember = ref(false); // 是否是企业成员
  // 企业成员的权限级别
  const enterpriseMemberLevel = ref(0); // 默认值为0表示不是企业成员 权限等级与项目的一致
  // 穿透模式
  const mgntMode = ref(0); //  1是全模式 2是自定义 3仅继承
  const refresh = () => {
    /** 重定向跳转时强刷使用 */
    const info = localStorage.getItem('userInfo');
    isLogin.value = Boolean(localStorage.getItem('access_token'));
    const userData = info ? JSON.parse(info) : {};
    for (const key in userData) {
      accountInfo[key] = userData[key];
    }
    accountInfo.access_token = localStorage.getItem('access_token') || '';
    accountInfo.refresh_token = localStorage.getItem('refresh_token') || '';
    accountInfo.xauth_token = localStorage.getItem('xauth_token') || '';
  };

  const saveStatus = (status: boolean) => {
    isLogin.value = status;
  };

  const saveAccountInfo = (info?: AccountInfo) => {
    if (info) {
      for (const key in info) {
        accountInfo[key] = info[key];
      }
    } else {
      for (const key in accountInfo) {
        accountInfo[key] = '';
      }
    }
  };

  const scoreTipsRes = async () => {
    const tipRes = await getScoreTips();
    const { need_tips, style_tips } = tipRes.data || {};
    const needTips = need_tips;
    saveAccountInfo({ need_tips: needTips, style_tips });
    localStorage.setItem('userInfo', JSON.stringify(accountInfo));
  };
  const updateCheckProFile = (status: ProFileStatus) => {
    checkProFile.value = status;
  };

  const storageProjectIssuePrInfo = async () => {
    const res = await getProjectIssuePrInfo();
    const { created_issue, created_pr, created_project } = res?.data?.data || {};
    const haveRepositoryInfo = created_issue || created_pr || created_project;
    localStorage.setItem('repository_user', accountInfo.username || '');
    localStorage.setItem('project_issue_pr_info', haveRepositoryInfo);
    localStorage.setItem('project_issue_pr_info_time', new Date().getTime().toString());
    return haveRepositoryInfo;
  };

  const checkProjectIssuePrInfo = async () => {
    // 判断当前用户是不是第一次访问
    // const storageUsername = localStorage.getItem('repository_user');
    // if (storageUsername !== accountInfo.username) {
    //   const haveRepositoryInfo = await storageProjectIssuePrInfo();
    //   return haveRepositoryInfo;
    // }
    // 异步刷新
    storageProjectIssuePrInfo();
    // 七天内访问过，则不刷新
    const lastTime = localStorage.getItem('project_issue_pr_info_time');
    const haveRepositoryInfo = localStorage.getItem('project_issue_pr_info');
    const days7 = 7 * 24 * 60 * 60 * 1000;
    if (lastTime && new Date().getTime() - Number(lastTime) < days7) {
      return haveRepositoryInfo === 'true';
    }

    return false;
  };

  // 可能有从博客跳转过来的，通过接口判断是否登陆
  const checkIsLogin = async () => {
    const { data } = await getUserToken();
    const { RemoveInfo } = useAccount();
    if (data?.data) {
      const { access_token, refresh_token } = data.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      saveStatus(true);
      const userRes = await getUserInfo();
      if (userRes.data?.data) {
        saveAccountInfo({
          access_token: localStorage.getItem('access_token') || '',
          refresh_token: localStorage.getItem('refresh_token') || '',
          ...userRes.data?.data
        });
      }
      scoreTipsRes();
      return true;
    }
    RemoveInfo();
    return false;
  };
  const saveAtomgitUserName = (username: string) => {
    accountInfo.atomgit_username = username;
  };
  // 设置权限
  const setAccess = (val: accessType) => {
    access.value = val;
  };
  // 保存用户role_info信息
  const saveRoleInfo = (val: any) => {
    roleInfo.value = val;
  };
  // 设置企业权限
  const setEnterpriseAccess = (val: accessType) => {
    enterpriseAccess.value = val;
  };

  const enterpriseRole = ref(''); // 用户在企业中的角色名
  // 存储用户的企业角色
  const setEnterpriseRole = (val: string) => {
    enterpriseRole.value = val;
  };

  const saveUpdateAccessStatus = (flag: boolean) => {
    updateAccessStatus.value = flag;
  };
  const saveIsEnterpriseMember = (flag: boolean, level: number, mode?: number) => {
    isEnterpriseMember.value = flag;
    enterpriseMemberLevel.value = level;
    mgntMode.value = mode || 0;
  };
  // 更新权限
  const updateAccess = async (params: authListReqType) => {
    const res = await getUserAuthList(params);
    if (!res.error) {
      const { data } = res.data;
      setAccess(data);
    }
  };

  const setUserEmail = (email: string) => {
    accountInfo.email = email;
    userInfo.email = email;
    saveAccountInfo(accountInfo);
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
  };

  return {
    isLogin,
    accountInfo,
    noviceStatus,
    checkProFile,
    updateCheckProFile,
    checkIsLogin,
    checkProjectIssuePrInfo,
    saveStatus,
    refresh,
    saveAccountInfo,
    saveAtomgitUserName,
    access,
    enterpriseAccess,
    setEnterpriseAccess,
    updateAccessStatus,
    saveUpdateAccessStatus,
    setAccess,
    updateAccess,
    saveRoleInfo,
    roleInfo,
    isEnterpriseMember,
    enterpriseMemberLevel,
    saveIsEnterpriseMember,
    mgntMode,
    setUserEmail,
    enterpriseRole,
    setEnterpriseRole
  };
});

/** 用户 profile 数据 */
export const otherAccountStore = defineStore('otherInfo', () => {
  const isLogin = ref(Boolean(localStorage.getItem('access_token')));
  const userInfo = {};
  const accountInfo = reactive<AccountInfo & { profile?: Profile; }>({
    ...userInfo
  });

  const saveAccountInfo = (info?: AccountInfo & { profile?: Profile; }) => {
    if (info) {
      for (const key in info) {
        accountInfo[key] = info[key];
      }
    } else {
      for (const key in accountInfo) {
        accountInfo[key] = '';
      }
    }
  };

  const saveFollowed = (isFollowed?: boolean) => {
    accountInfo.isFollow = isFollowed;
  };

  return {
    isLogin,
    accountInfo,
    saveAccountInfo,
    saveFollowed
  };
});

export const entranceData = defineStore('entrance', () => {
  const repoList = ref([]);
  const orgList = ref([]);
  return {
    repoList,
    orgList
  };
});

/**
 * 用户notebook服务状态管理
 */
export const userNotebookStore = defineStore('notebookStore', () => {
  /** notebook数据 */
  const notebookInfo = ref<NotebookDetailResp>();
  /** notebook服务状态 */
  const status = computed(() => {
    if (notebookInfo.value?.status !== undefined) {
      return notebookInfo.value?.status;
    }
    return -1;
  });
  /** notebook服务 开启/关闭 加载中 */
  const loading = ref(false);

  /** 设置notebook服务状态 */
  const setStatus = (num: EnumNotebookStatus) => {
    if (notebookInfo.value?.notebook_id) {
      notebookInfo.value.status = num;
    }
  };
  /** 设置 notebook服务 开启/关闭 加载中 */
  const setLoading = (blob: boolean) => {
    loading.value = blob;
  };
  const calculationTypeEn = computed(() => {
    for (const key in EnumComputedType) {
      if (EnumComputedType[key] === notebookInfo.value?.calculation_type) {
        return key;
      }
    }
    return '';
  });

  /** 获取notebook数据 */
  const fetchNotebookDetail = async () => {
    loading.value = true;
    const result = await getNotebookDetail();
    if (result.data) {
      notebookInfo.value = result.data.data;
    }
    loading.value = false;
  };
  return {
    notebookInfo,
    status,
    loading,
    setStatus,
    setLoading,
    fetchNotebookDetail,
    calculationTypeEn
  };
});

if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useAccountStore, (import.meta as any).hot));
}
